import React, { useEffect } from 'react'
import style from './Cart.module.scss'
import { EcommerceResponse, IEcommerceQuestionSchema } from '../../types'
import { getSmallAssetPath } from '@aytm/lib/common/assets'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale'
import AddToCartButton from './AddToCartButton'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import AssetsGate from '../../components/AssetsGate'
import { LinesSkeleton } from './Skeleton'

interface ICartProps {
  response: EcommerceResponse
  schema: IEcommerceQuestionSchema
  totals?: number[]
  findability: boolean
  onCartClose: () => void
  onAddToCart: (qnt: number, index: number) => void
  onItemSelect: (index: number) => void
  onUpdateResponse?: (response: EcommerceResponse) => void
  checkout?: () => void
  scrollTop: () => void
}

const Cart = ({
  response,
  schema,
  totals,
  findability,
  onCartClose,
  onAddToCart,
  onItemSelect,
  onUpdateResponse,
  checkout,
  scrollTop
}: ICartProps) => {
  const timeline = useTimeline()
  const viewport = useViewport()
  const { t, languageTag } = useSurveyLocale()
  const na = schema.answers.find(answer => answer.na)
  // The lines wait for their thumbs behind outlines of the lines; the totals and checkout never wait.
  const lineImages = response.answers
    .filter(answer => answer.v > 0)
    .map(answer => {
      const image = schema.answers.find(product => product.id === answer.id)?.image
      return image && getSmallAssetPath(image)
    })

  useEffect(() => {
    scrollTop()
  }, [scrollTop])

  const onDeleteFromCart = (index: number) => {
    response.answers[index].v = 0
    timeline.log({
      type: 'delete_from_cart',
      ref: response.answers[index].id
    })
    onUpdateResponse(response)
  }

  const onNaSelect = () => {
    timeline.log({
      type: 'na_select',
      ref: na.id
    })
    const answers = response.answers.map(answer => {
      answer.v = answer.id === na.id ? 1 : 0
      return answer
    })
    response.answers = answers
    onUpdateResponse(response)
  }

  return (
    <div
      className={`${style.cartContainer} ${viewport.mobile ? style.cartContainerMobile : ''}`}
      data-testid="SW-EC-Cart"
    >
      {totals[0] > 0 && (
        <button className={style.back} onClick={onCartClose}>
          <i className="pi pi-chevron-left" />
          <span>{t('questions', 'ec', 'costco', 'back')}</span>
        </button>
      )}
      <h6
        className={`${style.cartHeader} ${totals[0] > 0 ? '' : style.cartHeaderWide}`}
        dangerouslySetInnerHTML={{
          __html: t<(total: number) => string>('questions', 'ec', 'costco', 'cartTotal')(totals[0])
        }}
      />
      {totals[0] > 0 ? (
        <>
          <section className={style.cart}>
            <div className={style.products}>
              <AssetsGate urls={lineImages} loader={<LinesSkeleton count={lineImages.length} />}>
                {response.answers.map(
                  (answer, index) =>
                    answer.v > 0 && (
                      <div className={style.product} key={answer.id} data-testid="SW-EC-CartItem">
                        {schema.answers[index].image ? (
                          <div
                            className={style.image}
                            role="img"
                            aria-label={schema.answers[index].image?.alt_text || t('questions', 'img', 'noAlt')}
                            style={{ backgroundImage: `url(${getSmallAssetPath(schema.answers[index].image)})` }}
                          />
                        ) : (
                          <div className={style.noImage} />
                        )}
                        <div className={style.info}>
                          <div
                            className={style.caption}
                            dangerouslySetInnerHTML={{ __html: schema.answers[index].text }}
                            onClick={() => onItemSelect(index)}
                          />
                          <div
                            className={style.code}
                            dangerouslySetInnerHTML={{
                              __html: t<(code: string) => string>('questions', 'ec', 'costco', 'code')(`187893${index}`)
                            }}
                          />

                          <div
                            className={style.price}
                            dangerouslySetInnerHTML={{
                              __html: formatPriceWithLocale(answer.price, schema.price_mask, languageTag, true, false)
                            }}
                          />
                          {0 != schema.answers[index].discounted_price &&
                            schema.answers[index].price > schema.answers[index].discounted_price && (
                              <div
                                className={style.off}
                                dangerouslySetInnerHTML={{
                                  __html: `${formatPriceWithLocale(
                                    schema.answers[index].price - schema.answers[index].discounted_price,
                                    schema.price_mask,
                                    languageTag,
                                    true,
                                    false
                                  )} ${t('questions', 'ec', 'costco', 'off')}`
                                }}
                              />
                            )}

                          <div className={style.buttonContainer}>
                            <div className={style.buttonWrapper}>
                              <AddToCartButton
                                quantity={answer.v}
                                findability={findability}
                                index={index}
                                onAddToCart={onAddToCart}
                              />
                            </div>
                            <div className={style.totalPrice}>
                              <div className={style.totalText}>{t('questions', 'ec', 'costco', 'total')}</div>
                              <div
                                className={style.price}
                                dangerouslySetInnerHTML={{
                                  __html: formatPriceWithLocale(
                                    answer.price * answer.v,
                                    schema.price_mask,
                                    languageTag,
                                    true,
                                    false
                                  )
                                }}
                              />
                            </div>
                          </div>
                          <button className={style.remove} onClick={() => onDeleteFromCart(index)}>
                            {t('questions', 'ec', 'costco', 'remove')}
                          </button>
                        </div>
                        <div className={style.delivery}>
                          <EmptyLink color="#0060A9">
                            <div className={`${style.shipping} ${style.shippingSelected}`}>
                              <div className={style.shippingType}>
                                {t('questions', 'ec', 'costco', 'standartShipping')}
                              </div>
                              <div className={style.estimate}>
                                {t('questions', 'ec', 'costco', 'estimatedDelivery')}
                              </div>
                              <div className={style.date}>
                                {new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toLocaleDateString(languageTag, {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </div>
                            </div>
                          </EmptyLink>
                          <EmptyLink color="#0060A9">
                            <div className={style.shipping}>
                              <div className={style.shippingType}>
                                {t('questions', 'ec', 'costco', 'expressShipping')}
                              </div>
                              <div className={style.estimate}>
                                {t('questions', 'ec', 'costco', 'estimatedDelivery')}
                              </div>
                              <div className={style.date}>
                                {new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toLocaleDateString(languageTag, {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </div>
                            </div>
                          </EmptyLink>
                          <EmptyLink color="#0060A9">
                            <div className={style.details}>{t('questions', 'ec', 'costco', 'deliveryDetails')}</div>
                          </EmptyLink>
                          <div className={style.buttons}>
                            <EmptyLink color="#0060A9">
                              <div className={style.details}>{t('questions', 'ec', 'costco', 'addToList')}</div>
                            </EmptyLink>
                            <div className={style.line}>|</div>
                            <EmptyLink color="#0060A9">
                              <div className={style.details}>{t('questions', 'ec', 'costco', 'saveForLater')}</div>
                            </EmptyLink>
                          </div>
                        </div>
                      </div>
                    )
                )}
              </AssetsGate>
            </div>
            <div className={style.checkout}>
              <div className={style.checkoutRow}>
                <div className={style.regularText}>{t('questions', 'ec', 'costco', 'subtotal')}</div>
                <div
                  className={style.regularText}
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false)
                  }}
                />
              </div>
              <div className={style.checkoutRow}>
                <div className={style.smallText}>
                  {t('questions', 'ec', 'costco', 'handling')}
                  <br></br>
                  <span className={style.zip}>
                    <EmptyLink color="#0060A9">{t('questions', 'ec', 'costco', 'myIndex')}</EmptyLink>
                  </span>
                </div>
                <div
                  className={style.smallText}
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(0, schema.price_mask, languageTag, true, false)
                  }}
                />
              </div>
              <div className={style.checkoutBlock}>
                <div className={style.lineHorizontal} />
                <div className={style.checkoutRow}>
                  <div className={style.bigText}>{t('questions', 'ec', 'costco', 'estimateTotal')}</div>
                  <div
                    className={style.bigText}
                    dangerouslySetInnerHTML={{
                      __html: formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false)
                    }}
                  />
                </div>
                <div className={style.disclamer}>{t('questions', 'ec', 'costco', 'taxes')}</div>
                <button className={style.checkoutButton} onClick={checkout}>
                  {t('questions', 'ec', 'costco', 'checkout')}
                </button>
              </div>
            </div>
            {viewport.mobile && (
              <button className={style.top} onClick={scrollTop}>
                {t('questions', 'ec', 'costco', 'top')}
              </button>
            )}
          </section>

          {viewport.mobile && (
            <div className={style.checkoutMobile}>
              <div className={style.checkoutRow}>
                <div className={style.bigText}>{t('questions', 'ec', 'costco', 'estimateTotal')}</div>
                <div
                  className={style.bigText}
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false)
                  }}
                />
              </div>
              <button className={style.checkoutButton} onClick={checkout}>
                {t('questions', 'ec', 'costco', 'checkout')}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className={style.emptyCart}>
          <h6 className={style.emptyCartText}>{t('questions', 'ec', 'costco', 'emptyCart')}</h6>
          <button className={style.emptyCartButton} onClick={onCartClose}>
            {t('questions', 'ec', 'costco', 'continueShopping')}
          </button>
          {na && (
            <button className={style.emptyCartButton} onClick={onNaSelect}>
              {na.text || t('questions', 'ec', 'na')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default Cart
